import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError, of, forkJoin } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { TipoNota } from './base-nota.types';

export interface ConsultaData {
  patient_id: number;
  doctor_id: number;
  type?: TipoNota;
  appointment_id?: number;
  censo_id?: number;
  antecedentes?: any;
  soap_subjetivo?: string;
  soap_objetivo?: string;
  soap_analisis?: string;
  soap_plan?: string;
  diagnoses_cie11?: any[];
  fecha_consulta: string;
  status?: 'abierta' | 'completada';
}

@Injectable({ providedIn: 'root' })
export class ExpedienteService {
  private apiUrl = `${environment.apiUrl}/expediente`;

  constructor(private http: HttpClient) {}

  /**
   * Obtiene la última consulta del paciente
   * @deprecated Usar getUltimaNotaPorTipo() en su lugar
   */
  getUltimaConsulta(patientId: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/pacientes/${patientId}/ultima-consulta`)
      .pipe(
        map((response: any) => response.data),
        catchError(this.handleError)
      );
  }

  /**
   * Obtiene la última nota del paciente filtrada por tipo
   * @param patientId ID del paciente
   * @param noteType Tipo de nota: 'consulta', 'urgencias', 'ingreso', 'evolucion', 'egreso'
   * @returns La última nota del tipo especificado o null si no existe
   */
  getUltimaNotaPorTipo(patientId: number, noteType: string): Observable<any> {
    return this.getNotas(patientId).pipe(
      map((notas: any[]) => {
        if (!notas || notas.length === 0) {
          return null;
        }

        // Filtrar por tipo y ordenar por fecha descendente
        const notasFiltradas = notas
          .filter(nota => nota.type === noteType)
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

        return notasFiltradas.length > 0 ? notasFiltradas[0] : null;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Obtiene la última nota del paciente independientemente del tipo
   * Útil para la funcionalidad "Cargar Última Consulta" que debe buscar la última nota sin importar su tipo
   * @param patientId ID del paciente
   * @returns La última nota creada o null si no existe
   */
  getUltimaNotaDelPaciente(patientId: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/pacientes/${patientId}/ultima-nota`)
      .pipe(
        map((response: any) => response.data),
        catchError(this.handleError)
      );
  }

  /**
   * Obtiene la última nota del paciente que tenga secciones de antecedentes
   * Busca en todos los tipos de nota: consulta, urgencias, ingreso, evolucion, egreso
   * Las notas de evolucion y egreso heredan antecedentes de notas previas
   * @param patientId ID del paciente
   * @returns La última nota con antecedentes o null si no existe
   */
  getUltimaNotaConAntecedentes(patientId: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/pacientes/${patientId}/ultima-nota-con-antecedentes`)
      .pipe(
        map((response: any) => response.data),
        catchError(this.handleError)
      );
  }

  /**
   * Obtiene el historial de consultas del paciente
   */
  getHistorial(patientId: number): Observable<any[]> {
    return this.http.get(`${this.apiUrl}/pacientes/${patientId}/historial`)
      .pipe(
        map((response: any) => response.data),
        catchError(this.handleError)
      );
  }

  /**
   * Obtiene todas las notas del paciente
   */
  getNotas(patientId: number): Observable<any[]> {
    return this.http.get(`${this.apiUrl}/pacientes/${patientId}/notas`)
      .pipe(
        map((response: any) => response.data),
        catchError(this.handleError)
      );
  }

  /**
   * Obtiene una nota específica por su ID
   */
  getNota(noteId: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/notas/${noteId}`)
      .pipe(
        map((response: any) => response.data),
        catchError(this.handleError)
      );
  }

  /**
   * Obtiene el conteo de notas por tipo para un paciente
   * Útil para calcular el número de la próxima nota
   */
  getNotasCountByType(patientId: number, type: string): Observable<number> {
    return this.getNotas(patientId).pipe(
      map((notas: any[]) => {
        if (!notas || notas.length === 0) {
          return 0;
        }
        return notas.filter(nota => nota.type === type).length;
      }),
      catchError(() => of(0))
    );
  }

  /**
   * Crea una nueva nota/consulta
   */
  createNota(data: ConsultaData): Observable<any> {
    return this.http.post(`${this.apiUrl}/notas`, data)
      .pipe(
        map((response: any) => response), // ✅ Retorna respuesta completa {success, message, data}
        catchError(this.handleError)
      );
  }

  /**
   * Actualiza una nota existente
   */
  updateNota(noteId: number, data: Partial<ConsultaData>): Observable<any> {
    return this.http.put(`${this.apiUrl}/notas/${noteId}`, data)
      .pipe(
        map((response: any) => response),
        catchError(this.handleError)
      );
  }

  /**
   * Cancela una nota medica existente
   * @param noteId ID de la nota a cancelar
   * @param reason Motivo de la cancelacion (obligatorio)
   */
  cancelarNota(noteId: number, reason: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/notas/${noteId}/cancel`, { cancellation_reason: reason })
      .pipe(
        map((response: any) => response),
        catchError(this.handleError)
      );
  }

  /**
   * Obtiene los últimos signos vitales del paciente desde cualquier tipo de nota
   * Busca en todas las notas del paciente y retorna los signos vitales más recientes
   * @param patientId ID del paciente
   * @returns Objeto con los signos vitales o null si no hay datos
   */
  getUltimosSignosVitales(patientId: number): Observable<any | null> {
    return this.getNotas(patientId).pipe(
      map((notas: any[]) => {
        if (!notas || notas.length === 0) {
          return null;
        }

        // Ordenar por fecha descendente (más reciente primero)
        const notasOrdenadas = notas.sort((a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );

        // Buscar la primera nota que tenga signos vitales con datos
        for (const nota of notasOrdenadas) {
          if (nota.vitalSigns && Array.isArray(nota.vitalSigns) && nota.vitalSigns.length > 0) {
            const ultimoSigno = nota.vitalSigns[nota.vitalSigns.length - 1];

            // Mapear del formato backend al formato frontend
            return {
              ta_sistolica: ultimoSigno.blood_pressure_systolic || null,
              ta_diastolica: ultimoSigno.blood_pressure_diastolic || null,
              fc: ultimoSigno.heart_rate || null,
              fr: ultimoSigno.respiratory_rate || null,
              temp: ultimoSigno.temperature || null,
              spo2: ultimoSigno.oxygen_saturation || null,
              peso: ultimoSigno.weight || null,
              talla_cm: ultimoSigno.height || null,
              imc: ultimoSigno.bmi || null,
            };
          }
        }

        return null;
      }),
      catchError((error) => {
        console.error('[ExpedienteService] Error obteniendo últimos signos vitales:', error);
        return [null];
      })
    );
  }

  /**
   * Obtiene los medicamentos actuales del paciente desde la ultima nota medica
   * Busca en todas las notas del paciente y retorna los medicamentos de la mas reciente
   * @param patientId ID del paciente
   * @returns String con los medicamentos actuales o string vacio si no hay datos
   */
  getMedicamentosActuales(patientId: number): Observable<string> {
    return this.getNotas(patientId).pipe(
      map((notas: any[]) => {
        if (!notas || notas.length === 0) {
          return '';
        }

        // Ordenar por fecha descendente (mas reciente primero)
        const notasOrdenadas = notas.sort((a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );

        // Buscar la primera nota que tenga medicamentos actuales
        for (const nota of notasOrdenadas) {
          // Buscar en medicamentos_actuales (campo directo en medical_records)
          if (nota.medicamentos_actuales) {
            console.log('[ExpedienteService] Medicamentos encontrados en nota:', nota.id);
            return nota.medicamentos_actuales;
          }
        }

        return '';
      }),
      catchError((error) => {
        console.error('[ExpedienteService] Error obteniendo medicamentos actuales:', error);
        return [''];
      })
    );
  }

  /**
   * Obtiene TODAS las recetas de la última nota médica del paciente
   * Si hay recetas de texto libre y formulario en la misma nota, combina ambas
   * @param patientId ID del paciente
   * @param currentNoteId ID de la nota actual (opcional) para no marcar sus items como históricos
   * @returns Array de medicamentos combinados de todas las recetas de la última nota
   */
  getUltimaRecetaPaciente(patientId: number, currentNoteId?: number): Observable<any[]> {
    return this.getHistorialItemsDelPaciente(patientId, currentNoteId).pipe(
      map((historial) => {
        const indicaciones = historial.indicaciones_medicas || [];
        if (indicaciones.length === 0) {
          return [];
        }

        const recetas = indicaciones.filter((ind: any) => ind.tipo === 'receta');
        if (recetas.length === 0) {
          return [];
        }

        // Ordenar por fecha de creación descendente (más recientes primero)
        const recetasOrdenadas = [...recetas].sort((a, b) => {
          const fechaA = new Date(a.fecha_creacion || 0).getTime();
          const fechaB = new Date(b.fecha_creacion || 0).getTime();
          return fechaB - fechaA;
        });

        // Identificar la nota de origen más reciente
        const ultimaReceta = recetasOrdenadas[0];
        const notaOrigenId = ultimaReceta.nota_origen_id;

        console.log('[ExpedienteService] Última nota con recetas:', notaOrigenId);

        // Obtener TODAS las recetas de esa nota
        const recetasDeLaUltimaNota = recetasOrdenadas.filter(
          (r: any) => r.nota_origen_id === notaOrigenId
        );

        console.log('[ExpedienteService] Total recetas de la última nota:', recetasDeLaUltimaNota.length);

        // Combinar medicamentos de todas las recetas de la última nota
        const medicamentosCombinados: any[] = [];

        for (const receta of recetasDeLaUltimaNota) {
          const recetaFecha = receta.fecha_creacion || receta.created_at || null;
          const recetaId = receta.backendId || receta.id || null;
          const recetaNombre = receta.nombre || 'Receta';

          if (receta.modo_entrada === 'texto' && receta.contenido_html) {
            // Para recetas en modo texto libre, agregar como objeto especial
            medicamentosCombinados.push({
              texto_libre: receta.contenido_html,
              receta_fecha: recetaFecha,
              receta_id: recetaId,
              receta_nombre: recetaNombre,
            });
            console.log('[ExpedienteService] Receta en modo texto libre agregada');
          } else if (receta.medicamentos && receta.medicamentos.length > 0) {
            // Para recetas en modo formulario, agregar todos los medicamentos
            const medicamentosConMeta = receta.medicamentos.map((med: any) => ({
              ...med,
              receta_fecha: recetaFecha,
              receta_id: recetaId,
              receta_nombre: recetaNombre,
            }));
            medicamentosCombinados.push(...medicamentosConMeta);
            console.log('[ExpedienteService] Medicamentos de formulario agregados:', receta.medicamentos.length);
          } else if (receta.contenido?.medicamentos && receta.contenido.medicamentos.length > 0) {
            const medicamentosContenidoConMeta = receta.contenido.medicamentos.map((med: any) => ({
              ...med,
              receta_fecha: recetaFecha,
              receta_id: recetaId,
              receta_nombre: recetaNombre,
            }));
            medicamentosCombinados.push(...medicamentosContenidoConMeta);
          }
        }

        console.log('[ExpedienteService] Total medicamentos combinados:', medicamentosCombinados.length);
        return medicamentosCombinados;
      }),
      catchError((error) => {
        console.error('[ExpedienteService] Error obteniendo última receta:', error);
        return of([]);
      })
    );
  }

  /**
   * Obtiene TODAS las indicaciones medicas del paciente desde la tabla de prescriptions
   * @param patientId ID del paciente
   * @param currentNoteId ID de la nota actual (opcional) para no marcar sus items como históricos
   * @returns Array de indicaciones medicas (recetas completas) o array vacio
   */
  getIndicacionesMedicasDelPaciente(patientId: number, currentNoteId?: number): Observable<any[]> {
    return this.getHistorialItemsDelPaciente(patientId, currentNoteId).pipe(
      map((historial) => {
        const indicaciones = historial.indicaciones_medicas || [];
        if (indicaciones.length > 0) {
          console.log('[ExpedienteService] Indicaciones medicas encontradas - Total:', indicaciones.length);
        } else {
          console.log('[ExpedienteService] No se encontraron indicaciones medicas');
        }
        return indicaciones;
      }),
      catchError((error) => {
        console.error('[ExpedienteService] Error obteniendo indicaciones medicas:', error);
        return of([]);
      })
    );
  }

  getHistorialItemsDelPaciente(patientId: number, currentNoteId?: number): Observable<{
    solicitudes: any[];
    interconsultas: any[];
    escalas_clinicas: any[];
    indicaciones_hospitalarias: any[];
    indicaciones_medicas: any[];
  }> {
    let url = `${this.apiUrl}/pacientes/${patientId}/historial-items`;
    if (currentNoteId) {
      url += `?current_note_id=${currentNoteId}`;
    }
    return this.http.get(url)
      .pipe(
        map((response: any) => response.data || { solicitudes: [], interconsultas: [], escalas_clinicas: [], indicaciones_hospitalarias: [], indicaciones_medicas: [] }),
        catchError((error) => {
          console.error('[ExpedienteService] Error obteniendo historial items:', error);
          return of({ solicitudes: [], interconsultas: [], escalas_clinicas: [], indicaciones_hospitalarias: [], indicaciones_medicas: [] });
        })
      );
  }

  /**
   * Manejo centralizado de errores
   */
  /**
   * Obtiene TODOS los diagnósticos CIE-11 de TODAS las notas del paciente
   * Agrega diagnósticos de todas las notas sin duplicados (basado en código)
   * @param patientId ID del paciente
   * @returns Array de diagnósticos únicos ordenados por fecha (más reciente primero)
   */
  getAllDiagnosticosPaciente(patientId: number): Observable<any[]> {
    return this.getNotas(patientId).pipe(
      map((notas: any[]) => {
        if (!notas || notas.length === 0) {
          return [];
        }

        // Mapa para evitar duplicados (por código)
        const diagnosticosMap = new Map<string, any>();

        // Ordenar notas por fecha descendente
        const notasOrdenadas = notas.sort((a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );

        // Recorrer todas las notas y agregar diagnósticos
        for (const nota of notasOrdenadas) {
          if (nota.diagnoses_cie11 && Array.isArray(nota.diagnoses_cie11)) {
            for (const dx of nota.diagnoses_cie11) {
              const codigo = dx.code || dx.codigo || dx.cie10;
              if (codigo && !diagnosticosMap.has(codigo)) {
                diagnosticosMap.set(codigo, {
                  ...dx,
                  nota_id: nota.id,
                  nota_type: nota.type,
                  nota_fecha: nota.created_at
                });
              }
            }
          }
        }

        const resultado = Array.from(diagnosticosMap.values());
        console.log('[ExpedienteService] Diagnósticos totales del paciente:', resultado.length);
        return resultado;
      }),
      catchError((error) => {
        console.error('[ExpedienteService] Error obteniendo diagnósticos del paciente:', error);
        return of([]);
      })
    );
  }

  /**
   * Obtiene el historial completo de secciones NO enriquecidas de todas las notas del paciente.
   * Marca cada item con esHistorico: true para distinguirlos de items nuevos.
   * Usado para cargar automáticamente el historial al abrir una nueva nota.
   *
   * Secciones incluidas:
   * - Diagnósticos CIE-11
   * - Solicitudes (laboratorio, gabinete, internos)
   * - Interconsultas
   * - Escalas clínicas
   * - Indicaciones (recetas/hospitalarias)
   * - Paraclínicos
   *
   * @param patientId ID del paciente
   * @param excludeNotaId ID de la nota actual a excluir (para no duplicar datos de la nota siendo editada)
   * @returns Observable con objeto que contiene arrays de cada tipo de item histórico
   */
  getHistorialSeccionesFromNotas(patientId: number, excludeNotaId?: number): Observable<{
    diagnosticos: any[];
    solicitudes: any[];
    interconsultas: any[];
    escalas_clinicas: any[];
    indicaciones_medicas: any[];
    indicaciones_hospitalarias: any[];
    paraclinicos: any[];
  }> {
    return forkJoin({
      notas: this.getNotas(patientId),
      historialItems: this.getHistorialItemsDelPaciente(patientId, excludeNotaId)
    }).pipe(
      map(({ notas, historialItems }) => {
        const resultado = {
          diagnosticos: [] as any[],
          solicitudes: [] as any[],
          interconsultas: [] as any[],
          escalas_clinicas: [] as any[],
          indicaciones_medicas: [] as any[],
          indicaciones_hospitalarias: [] as any[],
          paraclinicos: [] as any[],
        };

        if (notas && notas.length > 0) {
          const notasFiltradas = excludeNotaId
            ? notas.filter(n => n.id !== excludeNotaId)
            : notas;

          const notasOrdenadas = notasFiltradas.sort((a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );

          const diagnosticosMap = new Map<string, any>();

          for (const nota of notasOrdenadas) {
            const notaInfo = {
              nota_origen_id: nota.id,
              nota_type: nota.type || nota.note_type,
              nota_fecha: nota.created_at,
            };

            if (nota.diagnoses_cie11 && Array.isArray(nota.diagnoses_cie11)) {
              for (const dx of nota.diagnoses_cie11) {
                const codigo = dx.code || dx.codigo || dx.cie10;
                if (codigo && !diagnosticosMap.has(codigo)) {
                  diagnosticosMap.set(codigo, {
                    ...dx,
                    cie10: codigo,
                    descripcion: dx.description || dx.title || dx.descripcion,
                    esHistorico: true,
                    ...notaInfo,
                  });
                }
              }
            }

            const escalas = nota.escalas_clinicas;
            if (escalas && Array.isArray(escalas)) {
              for (const escala of escalas) {
                resultado.escalas_clinicas.push({
                  ...escala,
                  esHistorico: true,
                  ...notaInfo,
                });
              }
            }
          }

          resultado.diagnosticos = Array.from(diagnosticosMap.values());
        }

        if (historialItems) {
          const filterByExcludeId = (items: any[]) => {
            if (!excludeNotaId) return items;
            return items.filter(item =>
              item.medical_record_id !== excludeNotaId && item.nota_origen_id !== excludeNotaId
            );
          };

          resultado.solicitudes = filterByExcludeId(historialItems.solicitudes || []).map(item => {
            return {
              ...item,
              nombre: item.nombre || item.titulo || 'Solicitud',
              tipo: item.tipo || 'laboratorio',
              esHistorico: true,
            };
          });

          resultado.interconsultas = filterByExcludeId(historialItems.interconsultas || []).map(item => {
            return {
              ...item,
              titulo: item.titulo || item.nombre || item.especialidad || 'Interconsulta',
              esHistorico: true,
            };
          });

          resultado.indicaciones_hospitalarias = filterByExcludeId(historialItems.indicaciones_hospitalarias || []).map(item => ({
            ...item,
            esHistorico: true,
          }));

          resultado.indicaciones_medicas = filterByExcludeId(historialItems.indicaciones_medicas || []).map(item => {
            const fecha = item.created_at || item.fecha_creacion;
            const fechaObj = fecha ? new Date(fecha) : new Date();
            const fechaFormateada = `${fechaObj.getDate()}/${fechaObj.getMonth() + 1}/${fechaObj.getFullYear()}`;
            return {
              ...item,
              nombre: `R-${fechaFormateada}`,
              esHistorico: true,
              finalizada: true,
            };
          });
        }

        console.log('[ExpedienteService] Historial cargado:', {
          diagnosticos: resultado.diagnosticos.length,
          solicitudes: resultado.solicitudes.length,
          interconsultas: resultado.interconsultas.length,
          escalas_clinicas: resultado.escalas_clinicas.length,
          indicaciones_medicas: resultado.indicaciones_medicas.length,
          indicaciones_hospitalarias: resultado.indicaciones_hospitalarias.length,
          paraclinicos: resultado.paraclinicos.length,
        });

        return resultado;
      }),
      catchError((error) => {
        console.error('[ExpedienteService] Error obteniendo historial de secciones:', error);
        return of({
          diagnosticos: [],
          solicitudes: [],
          interconsultas: [],
          escalas_clinicas: [],
          indicaciones_medicas: [],
          indicaciones_hospitalarias: [],
          paraclinicos: [],
        });
      })
    );
  }

  /**
   * Busca diagnósticos CIE-10/CIE-11 por término
   * @param termino Término de búsqueda (código o descripción)
   * @returns Observable con array de resultados {codigo, descripcion}
   */
  buscarCIE10(termino: string): Observable<any[]> {
    const baseUrl = environment.apiUrl.replace('/api', '');
    return this.http.get<any>(`${baseUrl}/api/cie10/search`, {
      params: { q: termino, lang: 'es', limit: '30' }
    }).pipe(
      map((response: any) => {
        if (response?.success && response?.data) {
          return response.data.map((item: any) => ({
            codigo: item.code,
            descripcion: item.description,
          }));
        }
        return [];
      }),
      catchError((error) => {
        console.error('[ExpedienteService] Error buscando CIE-10:', error);
        return of([]);
      })
    );
  }

  // ============================================
  // INDICACIONES HOSPITALARIAS (Nueva tabla normalizada)
  // ============================================

  /**
   * Obtiene las indicaciones hospitalarias de una nota médica específica
   * @param medicalRecordId ID de la nota médica
   * @returns Observable con array de indicaciones hospitalarias
   */
  getIndicacionesHospitalarias(medicalRecordId: number): Observable<any[]> {
    return this.http.get(`${this.apiUrl}/notas/${medicalRecordId}/indicaciones-hospitalarias`)
      .pipe(
        map((response: any) => response.data || []),
        catchError((error) => {
          console.error('[ExpedienteService] Error obteniendo indicaciones hospitalarias:', error);
          return of([]);
        })
      );
  }

  /**
   * Obtiene las indicaciones hospitalarias de un censo hospitalario
   * @param censoId ID del censo hospitalario
   * @returns Observable con array de indicaciones hospitalarias
   */
  getIndicacionesHospitalariasByCenso(censoId: number): Observable<any[]> {
    return this.http.get(`${this.apiUrl}/censo/${censoId}/indicaciones-hospitalarias`)
      .pipe(
        map((response: any) => response.data || []),
        catchError((error) => {
          console.error('[ExpedienteService] Error obteniendo indicaciones hospitalarias del censo:', error);
          return of([]);
        })
      );
  }

  /**
   * Crea una nueva indicación hospitalaria
   * @param data Datos de la indicación hospitalaria
   * @returns Observable con la indicación creada
   */
  createIndicacionHospitalaria(data: {
    medical_record_id: number;
    censo_id?: number;
    patient_id: number;
    tipo: string;
    nombre: string;
    contenido?: any;
    finalizada?: boolean;
  }): Observable<any> {
    const baseUrl = environment.apiUrl.replace('/api', '');
    return this.http.post(`${baseUrl}/api/indicaciones-hospitalarias`, data)
      .pipe(
        map((response: any) => response.data),
        catchError(this.handleError)
      );
  }

  /**
   * Crea múltiples indicaciones hospitalarias en lote
   * @param indicaciones Array de indicaciones hospitalarias
   * @returns Observable con las indicaciones creadas
   */
  bulkCreateIndicacionesHospitalarias(indicaciones: {
    medical_record_id: number;
    censo_id?: number;
    patient_id: number;
    tipo: string;
    nombre: string;
    contenido?: any;
    finalizada?: boolean;
  }[]): Observable<any> {
    const baseUrl = environment.apiUrl.replace('/api', '');
    return this.http.post(`${baseUrl}/api/indicaciones-hospitalarias/bulk`, { indicaciones })
      .pipe(
        map((response: any) => response.data),
        catchError(this.handleError)
      );
  }

  /**
   * Actualiza una indicación hospitalaria existente
   * @param id ID de la indicación hospitalaria
   * @param data Datos a actualizar
   * @returns Observable con la indicación actualizada
   */
  updateIndicacionHospitalaria(id: number, data: {
    tipo?: string;
    nombre?: string;
    contenido?: any;
    finalizada?: boolean;
  }): Observable<any> {
    const baseUrl = environment.apiUrl.replace('/api', '');
    return this.http.put(`${baseUrl}/api/indicaciones-hospitalarias/${id}`, data)
      .pipe(
        map((response: any) => response.data),
        catchError(this.handleError)
      );
  }

  /**
   * Elimina una indicación hospitalaria
   * @param id ID de la indicación hospitalaria
   * @returns Observable con el resultado
   */
  deleteIndicacionHospitalaria(id: number): Observable<any> {
    const baseUrl = environment.apiUrl.replace('/api', '');
    return this.http.delete(`${baseUrl}/api/indicaciones-hospitalarias/${id}`)
      .pipe(
        map((response: any) => response),
        catchError(this.handleError)
      );
  }

  /**
   * Marca una indicación hospitalaria como finalizada
   * @param id ID de la indicación hospitalaria
   * @returns Observable con la indicación actualizada
   */
  marcarIndicacionFinalizada(id: number): Observable<any> {
    return this.updateIndicacionHospitalaria(id, { finalizada: true });
  }

  // ============================================
  // ANTECEDENTES
  // ============================================

  /**
   * Obtiene los antecedentes del paciente.
   * Los antecedentes se almacenan a nivel de paciente (no de nota) como fuente de verdad.
   * Si no existen en el paciente, hace fallback a la última nota con antecedentes.
   * @param patientId ID del paciente
   * @returns Observable con los antecedentes del paciente
   */
  getPatientAntecedentes(patientId: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/pacientes/${patientId}/antecedentes`)
      .pipe(
        map((response: any) => {
          console.log('[ExpedienteService] Antecedentes del paciente cargados:', response.data);
          return response.data || {};
        }),
        catchError((error) => {
          console.error('[ExpedienteService] Error obteniendo antecedentes del paciente:', error);
          return of({});
        })
      );
  }

  /**
   * Actualiza los antecedentes del paciente.
   * Los antecedentes se guardan a nivel de paciente como fuente de verdad única.
   * @param patientId ID del paciente
   * @param antecedentes Objeto con los antecedentes a guardar
   * @returns Observable con los antecedentes actualizados
   */
  updatePatientAntecedentes(patientId: number, antecedentes: {
    heredofamiliares?: any;
    patologicos?: any;
    no_patologicos?: any;
    gineco_obstetricos?: any;
  }): Observable<any> {
    return this.http.put(`${this.apiUrl}/pacientes/${patientId}/antecedentes`, antecedentes)
      .pipe(
        map((response: any) => {
          console.log('[ExpedienteService] Antecedentes del paciente actualizados:', response.data);
          return response.data;
        }),
        catchError((error) => {
          console.error('[ExpedienteService] Error actualizando antecedentes del paciente:', error);
          return this.handleError(error);
        })
      );
  }

  private handleError(error: any): Observable<never> {
    let errorMessage = 'Ocurrió un error en el servidor';

    if (error.status === 422 && error.error?.errors) {
      // Error de validación - extraer errores específicos
      const validationErrors = error.error.errors;
      const errorMessages: string[] = [];

      for (const field in validationErrors) {
        if (validationErrors[field] && Array.isArray(validationErrors[field])) {
          errorMessages.push(...validationErrors[field]);
        }
      }

      errorMessage = errorMessages.length > 0
        ? errorMessages.join(', ')
        : 'Error de validación';

      console.error('[ExpedienteService] Errores de validación:', validationErrors);
    } else if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.status === 0) {
      errorMessage = 'No se pudo conectar con el servidor';
    } else if (error.status === 401) {
      errorMessage = 'Sesión expirada, por favor inicia sesión nuevamente';
    } else if (error.status === 404) {
      errorMessage = 'Recurso no encontrado';
    }

    console.error('[ExpedienteService] Error:', errorMessage, error);
    return throwError(() => new Error(errorMessage));
  }
}
